import React, { useState, useRef } from 'react';
import { StickyNote, CalendarClock, AlertTriangle, Sun, Cloud, CloudSun, CloudRain, CloudSnow, CloudFog, Wind, Zap, Thermometer } from 'lucide-react';
import DayTooltip from './DayTooltip';
import { isRoughWeather } from './WeatherBadge';
import { JOB_DRAG_TYPE } from './UnscheduledPanel';
import { isWeekend } from '../../utils/date-utils';
import { getHolidayName } from '../../utils/holiday-calculator';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';
const SLATE  = '#3E5C76';
const MAX_PILLS = 3;

// Busier days get a deeper walnut wash so open days stand out at a glance.
// Index = job count, capped at the last entry.
export const BUSY_TINTS = ['', `${WALNUT}0D`, `${WALNUT}1A`, `${WALNUT}29`, `${WALNUT}3B`];
export function busyTint(jobCount) {
  return BUSY_TINTS[Math.min(jobCount, BUSY_TINTS.length - 1)];
}

// Status → accent color, mapped semantically onto the shared palette
// (no blue/purple in this app's palette, so statuses that were previously
// distinguished by hue alone now share a family where their original
// colors were already close/identical — e.g. prod & scheduled).
const STATUS_COLOR = {
  sold:                OCHRE,
  awaiting_production: OCHRE,
  in_production:       WALNUT,
  ready_for_install:   MOSS,
  scheduled:           WALNUT,
  completed:           MOSS,
  cancelled:           RUST,
};
const DEFAULT_STATUS_COLOR = STONE;

const WEATHER_ICON = {
  storm: Zap,
  snow: CloudSnow,
  rain: CloudRain,
  fog: CloudFog,
  'partly-cloudy': CloudSun,
  cloudy: Cloud,
  wind: Wind,
  sunny: Sun,
  unknown: Thermometer,
};

export default function DayCell({
  date,
  isCurrentMonth,
  isToday,
  jobs = [],
  events = [],
  note,
  weather,
  onClick,
  onJobClick,
  onEventClick,
  onJobDrop
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [dropHover, setDropHover] = useState(false);
  const isJobDrag = e => Array.from(e.dataTransfer?.types || []).includes(JOB_DRAG_TYPE);
  const cellRef = useRef(null);

  const dayNumber = new Date(date).getDate();
  const weekend = isWeekend(date);
  const holidayName = getHolidayName(date);
  const isHolidayDay = !!holidayName;
  const totalJobs = jobs.length;

  // Filter note same as iOS: audience=internal && includeIn4amEmail
  const displayNote = note && note.audience === 'internal' && note.includeIn4amEmail
    ? note.text
    : null;

  // Background: drop target > how busy the day is > outside-month/weekend > default
  const baseBg = dropHover
    ? `${MOSS}26`
    : totalJobs > 0
      ? busyTint(totalJobs)
      : !isCurrentMonth || weekend
        ? `${INK}05`
        : isToday
          ? `${WALNUT}0A`
          : PAPER;

  // Weather is only ever a heads-up: flag it when jobs are booked on a rough day
  const weatherWarning = totalJobs > 0 && isRoughWeather(weather);

  const dateColor = isToday
    ? WALNUT
    : isHolidayDay
      ? RUST
      : weekend
        ? STONE
        : INK;

  const WeatherIcon = weather?.condition ? (WEATHER_ICON[weather.condition] || Thermometer) : null;

  return (
    <div
      ref={cellRef}
      onClick={(e) => { setShowTooltip(false); onClick && onClick(date, e); }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onDragOver={(e) => {
        if (!onJobDrop || !isJobDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!dropHover) setDropHover(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDropHover(false);
      }}
      onDrop={(e) => {
        if (!onJobDrop || !isJobDrag(e)) return;
        e.preventDefault();
        setDropHover(false);
        onJobDrop(date, e.dataTransfer.getData(JOB_DRAG_TYPE));
      }}
      style={{
        position: 'relative',
        minHeight: '120px',
        padding: '8px',
        background: baseBg,
        border: dropHover
          ? `2px dashed ${MOSS}`
          : isToday
            ? `2px solid ${WALNUT}`
            : `1px solid ${STONE}33`,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        opacity: isCurrentMonth ? 1 : 0.55,
        boxShadow: isToday ? `0 4px 12px ${WALNUT}26` : 'none'
      }}
      onMouseOver={(e) => {
        if (!isToday) e.currentTarget.style.boxShadow = `0 3px 10px ${WALNUT}1F`;
      }}
      onMouseOut={(e) => {
        if (!isToday) e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Day number */}
      <div style={{
        fontSize: '14px',
        fontWeight: isToday ? '700' : '600',
        color: dateColor,
        marginBottom: '6px'
      }}>
        {dayNumber}
      </div>

      {/* Weather chip — only on dates within the NWS forecast range */}
      {weather && (
        <div
          title={weatherWarning ? `Rough weather forecast — ${totalJobs} job${totalJobs === 1 ? '' : 's'} booked` : undefined}
          style={{
            position: 'absolute',
            top: '6px',
            right: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            background: weatherWarning ? `${RUST}1A` : `${WALNUT}0D`,
            border: weatherWarning ? `1px solid ${RUST}55` : '1px solid transparent',
            borderRadius: '6px',
            padding: '1px 4px',
          }}
        >
          {weatherWarning && (
            <AlertTriangle size={10} strokeWidth={2.5} color={RUST} />
          )}
          {WeatherIcon && (
            <WeatherIcon size={11} strokeWidth={2} color={weatherWarning ? RUST : STONE} />
          )}
          {weather.tempF != null && (
            <span style={{ fontSize: '10px', fontWeight: '700', color: weatherWarning ? RUST : STONE }}>
              {weather.tempF}°
            </span>
          )}
        </div>
      )}

      {/* Holiday name */}
      {isHolidayDay && (
        <div style={{
          fontSize: '10px',
          fontWeight: '600',
          color: RUST,
          borderBottom: `2px solid ${RUST}`,
          display: 'inline-block',
          paddingBottom: '2px',
          marginBottom: '6px'
        }}>
          {holidayName}
        </div>
      )}

      {/* Internal note callout */}
      {displayNote && (
        <div style={{
          fontSize: '11px',
          color: OCHRE,
          background: `${OCHRE}1A`,
          border: `1px solid ${OCHRE}66`,
          padding: '4px 8px',
          borderRadius: '6px',
          marginBottom: '6px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          <StickyNote size={12} strokeWidth={2} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayNote}</span>
        </div>
      )}

      {/* Imported .ics events, then job pills — MAX_PILLS shown in total;
          the rest are in the day pop-up. */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        marginTop: '4px'
      }}>
        {events.slice(0, MAX_PILLS).map(event => (
          <div
            key={event.id}
            onClick={(e) => { e.stopPropagation(); setShowTooltip(false); onEventClick && onEventClick(event); }}
            style={{
              background: `${SLATE}1A`,
              borderLeft: `2px solid ${SLATE}`,
              padding: '3px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: '600',
              color: SLATE,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              cursor: 'pointer'
            }}
            title={event.title}
          >
            <CalendarClock size={10} strokeWidth={2} style={{ flexShrink: 0 }} />
            {!event.allDay && (
              <span style={{ flexShrink: 0, opacity: 0.85 }}>
                {event.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(':00', '')}
              </span>
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.title}</span>
          </div>
        ))}

        {jobs.slice(0, Math.max(0, MAX_PILLS - events.length)).map((job, index) => {
          const status = (job.status || 'scheduled').toLowerCase();
          const accent = STATUS_COLOR[status] || DEFAULT_STATUS_COLOR;
          const contractNum = job.contractNumber || '';
          const location = job.name || job.location || '';

          return (
            <div
              key={index}
              onClick={(e) => { e.stopPropagation(); setShowTooltip(false); onJobClick && onJobClick(job); }}
              style={{
                cursor: 'pointer',
                background: `${accent}22`,
                padding: '3px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '600',
                color: accent,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                boxShadow: `0 1px 2px ${INK}1A`
              }}
              title={`${contractNum} - ${location}`}
            >
              {contractNum && (
                <span style={{ flexShrink: 0, opacity: 0.85 }}>{contractNum}</span>
              )}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{location}</span>
            </div>
          );
        })}

        {jobs.length + events.length > MAX_PILLS && (
          <div style={{
            fontSize: '9px',
            color: STONE,
            fontWeight: '600',
            paddingLeft: '6px'
          }}>
            +{jobs.length + events.length - MAX_PILLS} more
          </div>
        )}
      </div>

      {showTooltip && (totalJobs > 0 || displayNote || isHolidayDay) && cellRef.current && (
        <DayTooltip
          date={date}
          jobs={jobs}
          holidayName={holidayName}
          note={displayNote}
          cellRef={cellRef}
        />
      )}
    </div>
  );
}
